import React, { useState, useEffect } from 'react';
import { Modal, Input, Button, message } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useTheme } from '../../contexts/useTheme';

const { TextArea } = Input;

interface EditableTextModalProps {
  title: string;
  initialValue: string;
  onSave: (newValue: string) => Promise<void>;
  buttonSize?: 'small' | 'middle' | 'large';
  buttonStyle?: React.CSSProperties;
}

/**
 * A component that displays an edit button which opens a modal for editing text.
 * Used for root users to edit forecast text fields.
 */
export const EditableTextModal: React.FC<EditableTextModalProps> = ({
  title,
  initialValue,
  onSave,
  buttonSize = 'small',
  buttonStyle,
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editedValue, setEditedValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);

  // Reset edited value when modal opens or initial value changes
  useEffect(() => {
    if (isModalOpen) {
      setEditedValue(initialValue);
    }
  }, [isModalOpen, initialValue]);

  const handleOpen = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setEditedValue(initialValue);
  };

  const handleSave = async () => {
    // Don't save if nothing changed
    if (editedValue === initialValue) {
      setIsModalOpen(false);
      return;
    }

    setSaving(true);
    try {
      await onSave(editedValue);
      message.success('Text updated successfully');
      setIsModalOpen(false);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save changes';
      message.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Button
        type="text"
        size={buttonSize}
        icon={<EditOutlined />}
        onClick={handleOpen}
        style={{
          color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
          ...buttonStyle,
        }}
        title={`Edit ${title}`}
      />
      
      <Modal
        title={`Edit ${title}`}
        open={isModalOpen}
        onCancel={handleCancel}
        width={700}
        footer={[
          <Button key="cancel" onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>,
          <Button 
            key="save" 
            type="primary" 
            onClick={handleSave} 
            loading={saving}
            disabled={editedValue === initialValue}
          >
            Save Changes
          </Button>,
        ]}
      >
        <TextArea
          value={editedValue}
          onChange={(e) => setEditedValue(e.target.value)}
          rows={10}
          style={{
            resize: 'vertical',
            fontFamily: 'inherit',
          }}
          placeholder={`Enter ${title.toLowerCase()}...`}
          disabled={saving}
        />
        <div style={{ 
          marginTop: 8, 
          fontSize: 12, 
          color: isDark ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)' 
        }}>
          {editedValue.length} characters
        </div>
      </Modal>
    </>
  );
};

export default EditableTextModal;

